import classNames from 'classnames'
import { usePathname, useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, {
  CSSProperties,
  forwardRef,
  HTMLAttributeAnchorTarget,
  memo,
  ReactNode,
  useEffect,
  useState,
} from 'react'

import type { TAbsoluteLocale, TCountry } from '@src/config/localisation/locale-config.types'
import { Link } from '@src/modules/navigation'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'

import withTracking from '../../hocs/withTracking'

import styles from './link.module.scss'

const HEX_COLOR_CODE_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/

export interface LinkProps {
  children: ReactNode
  onClick?: (...args: any[]) => any
  href?: string | undefined
  className?: string
  disabled?: boolean
  target?: HTMLAttributeAnchorTarget
  withoutBaseStyle?: boolean
  enableEarlyFetch?: boolean
  style?: CSSProperties
  selectorKey?: string
  srOnlyLabel?: string
  textColor?: string | null
  id?: string
  hidePointerCursor?: boolean
  tabIndex?: number
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  locale?: '' | false | TCountry | TAbsoluteLocale
  'aria-label'?: string
  'data-testid'?: string
}

const checkExternalLink = (href?: string) => {
  if (!href) return true
  try {
    // During client-side navigation, window.location might be temporarily invalid
    // In such cases, assume it's an internal link to allow Next.js to handle navigation
    if (!window?.location?.origin) {
      return false
    }
    const url = new URL(href, window.location.origin)
    return url.origin !== window.location.origin
  } catch (e) {
    // If URL parsing fails, assume external link for safety
    return true
  }
}

const LinkWithOutTracking = forwardRef<LinkProps, any>(function LinkWithOutTrackingEl(
  {
    className,
    onClick,
    href,
    children,
    target,
    disabled,
    withoutBaseStyle = false,
    enableEarlyFetch = false,
    style,
    selectorKey,
    srOnlyLabel,
    textColor,
    id = '',
    hidePointerCursor = false,
    tabIndex,
    onMouseEnter,
    onMouseLeave,
    locale,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
  },
  ref: React.ForwardedRef<any>,
) {
  const { t } = useTranslation('common')
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isI18nPreviewEnabled = useI18nPreviewContext()

  const styleWithTextColor = textColor?.match(HEX_COLOR_CODE_REGEX)
    ? { ...style, color: textColor, backgroundImage: `linear-gradient(${textColor}, ${textColor})` }
    : style
  const [isExternalLink, setIsExternalLink] = useState(checkExternalLink.bind(null, href))

  useEffect(() => {
    setIsExternalLink(checkExternalLink(href))
  }, [href])

  /**
   * NOTE: NextLink doesn't play well with hash updates
   *  */
  const checkHashUpdate = () => {
    if (!href) return true
    try {
      // During client-side navigation, window.location might be temporarily invalid
      if (!window?.location?.origin) {
        return false
      }
      const targetURL = new URL(href, window.location.origin)
      const targetURLWithoutHash = targetURL.origin + targetURL.pathname + (targetURL.search || '?')
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const currentURLWithoutHash = window.location.origin + pathname + '?' + searchParams?.toString()
      const isSamePath = targetURLWithoutHash === currentURLWithoutHash
      const isSameHash = window.location.hash === targetURL.hash
      return isSamePath && !isSameHash
    } catch (error) {
      // If URL construction fails during navigation, return false
      return false
    }
  }

  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel || (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return isExternalLink || checkHashUpdate() ? (
    <a
      {...(id ? { id, ['data-testid']: id } : {})}
      // Explicit data-testid spreads after the id-derived one so it wins when both are set.
      {...(dataTestId ? { ['data-testid']: dataTestId } : {})}
      ref={ref}
      href={href}
      target={target}
      aria-label={computedAriaLabel}
      style={styleWithTextColor}
      className={classNames(className, !hidePointerCursor && 'cursor--pointer', {
        [styles.wrapper ?? '']: !withoutBaseStyle,
        [styles.disabled ?? '']: disabled,
      })}
      onClick={onClick}
      tabIndex={tabIndex}
      data-selector-key={selectorKey}
      aria-disabled={disabled ? 'true' : undefined}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </a>
  ) : (
    <Link
      {...(id ? { id, ['data-testid']: id } : {})}
      // Explicit data-testid spreads after the id-derived one so it wins when both are set.
      {...(dataTestId ? { ['data-testid']: dataTestId } : {})}
      ref={ref}
      href={href}
      target={target}
      aria-label={computedAriaLabel}
      style={styleWithTextColor}
      className={classNames(className, !hidePointerCursor && 'cursor--pointer', {
        [styles.wrapper ?? '']: !withoutBaseStyle,
        [styles.disabled ?? '']: disabled,
      })}
      onClick={onClick}
      aria-disabled={disabled ? 'true' : undefined}
      tabIndex={tabIndex ?? (disabled ? -1 : undefined)}
      data-selector-key={selectorKey}
      prefetch={enableEarlyFetch}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      locale={isI18nPreviewEnabled ? locale : undefined}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </Link>
  )
})

export { LinkWithOutTracking }

export default memo(withTracking(LinkWithOutTracking, true))
